import React, {useCallback} from 'react';
import {Link} from '@mui/material';
import type {Media} from 'services/media/types';
import {isFeatureEnabled} from 'configuration';
import {useDownloadMediaUrl} from 'services/media';
import {
  useDeleteMediaMutation,
  useGetMediaUrlQuery,
  useLazyGetMediaUrlQuery,
} from 'services/media/mediaApi';
import {useGetManifestationEditValue} from 'services/metadata';
import {Layout, ToolBar, ToolButton} from 'components';
import {ConfirmButton} from 'components/button/ConfirmButton';

type Props = {
  file: Media;
  manifestationId: string;
  mock?: boolean;
};

const fileThreshold = 200 * (1024 * 1024); // 200 MB

export const FileToolbar: React.FC<Props> = ({file, manifestationId}) => {
  const getEditValue = useGetManifestationEditValue();
  const eanValue = getEditValue('ean', manifestationId);
  const ean = typeof eanValue === 'string' ? eanValue : undefined;

  const downloadMediaUrl = useDownloadMediaUrl(file);
  const [deleteMediaMutation] = useDeleteMediaMutation();
  const {data: mediaUrl} = useGetMediaUrlQuery(file, {
    skip: !((file.size ?? 0) > fileThreshold),
  });
  const [getLazyMediaUrlQuery] = useLazyGetMediaUrlQuery();

  const handleDelete = useCallback(
    (subType: Media['subType'], fileNr: Media['fileNr']) => {
      return new Promise<void>(() => {
        if (!ean) {
          throw new Error('EAN is required to delete media');
        }
        return deleteMediaMutation({ean, media: {subType, fileNr}});
      });
    },
    [deleteMediaMutation, ean],
  );

  const canDelete =
    ean &&
    file.type === 'TRYKKUNDERLAG' &&
    !file.compressed &&
    isFeatureEnabled('deleteMedia');

  return (
    <Layout horizontal adjustCenter adjustRight sx={{width: '100%'}}>
      <ToolBar spacing="tight">
        {mediaUrl ? (
          <Link
            download={'download'}
            target="_blank"
            rel="noopener noreferrer"
            href={mediaUrl}
            underline="hover">
            Direktelink
          </Link>
        ) : null}

        {canDelete ? (
          <>
            <ConfirmButton
              onConfirm={() => handleDelete(file.subType, file.fileNr)}
              icon={'Delete'}
              iconOnly
              title={''}
            />
          </>
        ) : null}

        <ToolButton
          title={''}
          icon={'Download'}
          onClick={downloadMediaUrl}
          iconOnly
        />
        <ToolButton
          title={''}
          icon={'Preview'}
          onClick={() => {
            getLazyMediaUrlQuery(file)
              .unwrap()
              .then(url => {
                if (url) {
                  window.open(url, '_blank');
                }
              });
          }}
          iconOnly
        />
      </ToolBar>
    </Layout>
  );
};
